import { QueryClient } from "@tanstack/react-query";

const NON_RETRYABLE_MUTATION_STATUS_CODES = new Set([
  400, // Bad Request (validacion/negocio)
  401, // Unauthorized
  403, // Forbidden
  404, // Not Found
  405, // Method Not Allowed
  409, // Conflict (duplicados)
  410, // Gone
  412, // Precondition Failed
  413, // Payload Too Large
  415, // Unsupported Media Type
  422, // Unprocessable Entity
]);

function extractHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    cause?: unknown;
  };

  if (typeof candidate.status === "number") {
    return candidate.status;
  }

  if (typeof candidate.statusCode === "number") {
    return candidate.statusCode;
  }

  if (candidate.cause && typeof candidate.cause === "object") {
    const causeStatus = extractHttpStatus(candidate.cause);
    if (typeof causeStatus === "number") {
      return causeStatus;
    }
  }

  return undefined;
}

function shouldRetryMutationError(
  failureCount: number,
  error: unknown
): boolean {
  const status = extractHttpStatus(error);

  if (typeof status === "number") {
    if (NON_RETRYABLE_MUTATION_STATUS_CODES.has(status)) {
      return false;
    }

    // Cualquier 4xx no listado arriba se considera no recuperable,
    // salvo timeouts/rate-limit que sí pueden resolverse con backoff.
    if (status >= 400 && status < 500 && ![408, 425, 429].includes(status)) {
      return false;
    }

    // Reintenta solo status potencialmente transitorios.
    return failureCount < 2;
  }

  // Sin status explícito no se reintenta automáticamente.
  return false;
}

/**
 * Configuración de React Query optimizada para offline-first
 *
 * Estrategia:
 * - Prioriza datos en caché sobre peticiones de red
 * - Reintenta operaciones fallidas automáticamente
 * - Mantiene datos en caché durante más tiempo
 * - Usa modo 'offlineFirst' por defecto
 *
 * EN DESARROLLO: Cache deshabilitado para siempre obtener datos frescos
 */
export const createOfflineFirstQueryClient = () => {
  // Evaluar en runtime para evitar problemas de hidratación
  const isDevelopment = process.env.NODE_ENV === "development";

  return new QueryClient({
    defaultOptions: {
      queries: {
        // Network mode: en desarrollo siempre online, en producción offlineFirst
        networkMode: isDevelopment ? "online" : "offlineFirst",

        // Tiempo antes de considerar datos como obsoletos
        // En desarrollo: 0 (siempre obsoleto), en producción: 30 minutos
        staleTime: isDevelopment ? 0 : 1000 * 60 * 30,

        // Tiempo que se mantienen datos no usados en caché
        // En desarrollo: 0 (sin cache), en producción: 24 horas
        gcTime: isDevelopment ? 0 : 1000 * 60 * 60 * 24,

        // Reintentos automáticos en caso de fallo
        retry: (failureCount, error) => {
          // No reintentar errores de autenticación
          if (error && typeof error === "object" && "status" in error) {
            const status = (error as { status?: number }).status;
            if (status === 401 || status === 403) {
              return false;
            }
          }
          // Máximo 3 reintentos
          return failureCount < 3;
        },

        // Delay entre reintentos (backoff exponencial)
        retryDelay: attemptIndex => {
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },

        // Refetch al enfocar ventana (en desarrollo siempre, en producción no)
        refetchOnWindowFocus: isDevelopment,

        // Refetch cuando se reconecta
        refetchOnReconnect: true,

        // Refetch al montar (en desarrollo siempre, en producción no)
        refetchOnMount: isDevelopment,
      },
      mutations: {
        // Network mode para mutaciones
        networkMode: "offlineFirst",

        // Reintentos para mutaciones fallidas
        retry: shouldRetryMutationError,

        // Delay entre reintentos
        retryDelay: attemptIndex => {
          return Math.min(1000 * 2 ** attemptIndex, 10000);
        },
      },
    },
  });
};

/**
 * Configuración específica para consultas que siempre deben estar actualizadas
 */
export const alwaysOnlineQueryOptions = {
  networkMode: "online" as const,
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
};

/**
 * Configuración para datos estáticos que rara vez cambian
 */
export const staticDataQueryOptions = {
  networkMode: "offlineFirst" as const,
  staleTime: Infinity,
  gcTime: 1000 * 60 * 60 * 24 * 7, // 7 días
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

/**
 * Configuración para datos que se sincronizan en background
 */
export const backgroundSyncQueryOptions = {
  networkMode: "offlineFirst" as const,
  staleTime: 1000 * 60 * 60, // 1 hora
  gcTime: 1000 * 60 * 60 * 24, // 24 horas
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
};
